/**
 * Sequence Manager
 * Generates and validates sequences for Simon gameplay
 */

export type ButtonColor = 'red' | 'green' | 'blue' | 'yellow';

export interface SequenceStep {
  color: ButtonColor;
  timestamp: number; // Expected timing for rhythm mode (ms from sequence start)
}

export interface RhythmFeedback {
  precision: 'perfect' | 'good' | 'ok' | 'miss';
  delta: number; // Actual timing difference in ms
}

export interface IntruderButton {
  id: string;
  color: ButtonColor;
  position: { x: number; y: number };
  isDecoy: true;
  spawnTime: number;
}

export class SequenceManager {
  private sequence: SequenceStep[] = [];
  private currentIndex = 0;
  private rhythmStartTime = 0; // Track when rhythm input window started
  private rhythmLeadInMs = 0; // Grace period before first expected tap
  private playerTimestamps: number[] = []; // Track when player actually tapped
  private inputStartTime = 0; // Track when input phase started
  private responseTimes: number[] = []; // Track response time for each input

  /**
   * Generate a new random sequence of specified length
   */
  generate(length: number): SequenceStep[] {
    const colors: ButtonColor[] = ['red', 'green', 'blue', 'yellow'];
    this.sequence = Array.from({ length }, () => ({
      color: colors[Math.floor(Math.random() * colors.length)],
      timestamp: 0 // Will be set during playback
    }));
    this.currentIndex = 0;
    this.playerTimestamps = [];
    return this.sequence;
  }

  /**
   * Add one random step to existing sequence
   */
  extend(): SequenceStep[] {
    const colors: ButtonColor[] = ['red', 'green', 'blue', 'yellow'];
    this.sequence.push({
      color: colors[Math.floor(Math.random() * colors.length)],
      timestamp: 0
    });
    return this.sequence;
  }

  /**
   * Get the full sequence
   */
  getSequence(): SequenceStep[] {
    return [...this.sequence];
  }

  /**
   * Set rhythm timestamps for the sequence
   * Called after showing sequence to player
   */
  setRhythmTimestamps(timestamps: number[]): void {
    this.sequence.forEach((step, i) => {
      if (i < timestamps.length) {
        step.timestamp = timestamps[i];
      }
    });
  }

  /**
   * Mark the start of rhythm input phase
   */
  startRhythmCapture(leadInMs = 0): void {
    this.rhythmLeadInMs = leadInMs;
    this.rhythmStartTime = Date.now();
    this.playerTimestamps = [];
  }

  /**
   * Validate user input against expected sequence (classic mode)
   */
  validate(inputColor: ButtonColor): boolean {
    if (this.currentIndex >= this.sequence.length) {
      return false;
    }

    const isCorrect = this.sequence[this.currentIndex].color === inputColor;
    if (isCorrect) {
      this.currentIndex++;
    }

    return isCorrect;
  }

  /**
   * Validate rhythm input with timing evaluation
   * Returns feedback on precision
   */
  validateRhythm(inputColor: ButtonColor, toleranceMs = 150): RhythmFeedback | null {
    if (this.currentIndex >= this.sequence.length) {
      return null;
    }

    const expectedStep = this.sequence[this.currentIndex];
    const currentTime = Date.now();
    const playerTime = currentTime - this.rhythmStartTime;
    
    // Track player timestamp
    this.playerTimestamps.push(playerTime);

    // Color must match
    if (expectedStep.color !== inputColor) {
      return null; // Wrong color = failure
    }

    // Calculate timing delta
    const expectedTime = expectedStep.timestamp + this.rhythmLeadInMs;
    const delta = Math.abs(playerTime - expectedTime);

    // Determine precision level based on tolerance
    let precision: 'perfect' | 'good' | 'ok' | 'miss';
    if (delta <= toleranceMs * 0.3) {
      precision = 'perfect';
    } else if (delta <= toleranceMs * 0.6) {
      precision = 'good';
    } else if (delta <= toleranceMs) {
      precision = 'ok';
    } else {
      precision = 'miss';
    }

    this.currentIndex++;

    return { precision, delta };
  }

  /**
   * Check if sequence is complete
   */
  isComplete(): boolean {
    return this.currentIndex === this.sequence.length;
  }

  /**
   * Reset validation state
   */
  reset(): void {
    this.currentIndex = 0;
    this.playerTimestamps = [];
    this.responseTimes = [];
    this.rhythmLeadInMs = 0;
  }

  /**
   * Mark the start of input phase for panic mode timing
   */
  startInputPhase(): void {
    this.inputStartTime = Date.now();
    this.responseTimes = [];
  }

  /**
   * Record response time for current input
   * Returns the response time in ms
   */
  recordResponseTime(): number {
    const responseTime = Date.now() - this.inputStartTime;
    this.responseTimes.push(responseTime);
    this.inputStartTime = Date.now(); // Reset for next input
    return responseTime;
  }

  /**
   * Get all recorded response times
   */
  getResponseTimes(): number[] {
    return [...this.responseTimes];
  }

  /**
   * Calculate average response time
   */
  getAverageResponseTime(): number {
    if (this.responseTimes.length === 0) return 0;
    const sum = this.responseTimes.reduce((a, b) => a + b, 0);
    return sum / this.responseTimes.length;
  }

  /**
   * Get current sequence length
   */
  getLength(): number {
    return this.sequence.length;
  }

  /**
   * Get player timestamps for analysis
   */
  getPlayerTimestamps(): number[] {
    return [...this.playerTimestamps];
  }
}
